////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// ════════════════════════════════════════════════════════════════════════════════
// * POD - Privacy Policy - Logic and State Management
// * IRO Pattern: INPUT=none, REQUIREMENTS=manage privacy content state, OUTPUT=Pea with props
// ════════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import Pea_Privacy from '../pea/Bitsbound_Kings_Home_Primary_SRC_Privacy_Pea.js';

// ════════════════════════════════════════════════════════════════════════════════
// DEFINITIONS 'D' - Privacy Policy Content (100% Factual from Codebase)
// ════════════════════════════════════════════════════════════════════════════════

const PRIVACY_CONTENT = {
  overview: {
    title: "Privacy Overview",
    content: `BitsBound, Inc. ("BitsBound," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered contract analysis platform.

BitsBound provides AI-assisted contract analysis tools for legal professionals. All outputs are preliminary drafts requiring attorney review and do not constitute legal advice.

For detailed information about how we process data on behalf of our customers (as a data processor), please see our Data Processing Agreement (DPA) at bitsbound.com/dpa.

Last Updated: December 21, 2025
Effective Date: December 8, 2025`,
  },
  "data-collection": {
    title: "Information We Collect",
    content: `Account Information:
• Email address (serves as your username)
• First and last name
• Company name (legal entity name for B2B relationship)
• Password (stored as bcrypt hash, never in plaintext)

Profile Information (Google OAuth):
• Google user ID
• Email address and verification status
• Full name, first name, last name
• Profile picture URL
• Locale preference

Contract Data:
• Contract documents you upload (PDF, DOCX formats)
• Custom playbook files for analysis customization
• Analysis results and redlined documents
• Negotiation email drafts generated by our AI

Technical Information:
• IP address (for audit logging and geolocation)
• Browser type and device information
• Usage patterns and feature interactions`,
  },
  "data-storage": {
    title: "How We Store Your Data",
    content: `Cloud Infrastructure:
• MongoDB Atlas: All customer data and metadata stored with encryption at rest
• Cloudflare R2: Contract files and playbooks stored with secure access controls
• Customer-per-database architecture ensures complete data isolation between customers

Data Residency:
• You can choose your data cluster location: EU or US
• EU cluster for GDPR compliance (Article 44 requirements)
• Auto-detection available based on your IP address
• You can manually configure your preferred data location

Security Measures:
• bcrypt password hashing with 10+ salt rounds (NIST 800-63B compliant)
• SHA-256 hashing for data integrity verification
• Cryptographically secure random token generation
• All sensitive credentials stored as environment variables
• No credit card data stored on BitsBound servers`,
  },
  "data-usage": {
    title: "How We Use Your Information",
    content: `Service Delivery:
• Processing and analyzing contracts you upload
• Generating redlined documents with Track Changes
• Creating negotiation email drafts
• Applying your custom playbook rules during analysis

Account Management:
• Creating and managing your account
• Processing subscription payments through Stripe
• Sending transactional emails via Resend
• Customer support and communications

AI Processing:
• Your contract text is sent to Anthropic's Claude AI models for analysis
• Claude Haiku 4.5: Fast parallel analysis (16+ analyzers)
• Claude Sonnet 4.5: SAC (Supreme AI Co-Counsel) conversations
• Claude Opus 4.5: Precision redlining and commenting
• Contract content is processed but not used to train AI models

Security and Compliance:
• Audit logging for compliance and forensics
• Failed login attempt monitoring
• Session management with 7-day JWT token expiration`,
  },
  "third-parties": {
    title: "Third-Party Service Providers",
    content: `Infrastructure Providers:
• Vercel: Frontend application hosting
• Render: Backend API hosting
• MongoDB Atlas: Database hosting with built-in encryption

Payment Processing:
• Stripe: All payment processing (PCI DSS Level 1 compliant)
• Card details never touch BitsBound infrastructure
• Stripe handles all PCI compliance requirements

AI Services:
• Anthropic (Claude): Contract analysis and natural language processing
• Contract content processed for analysis only, not model training

Email Services:
• Resend: Transactional email delivery

Authentication:
• Google OAuth 2.0: Optional sign-in method
• Google receives only authentication requests, not your contract data

For a complete list of sub-processors and data transfer mechanisms, see our Data Processing Agreement at bitsbound.com/dpa.`,
  },
  "data-retention": {
    title: "Data Retention",
    content: `Active Accounts:
• Account data retained while your subscription is active
• Contract analyses retained for your reference and audit trail
• Session data expires after 7 days of inactivity

Account Closure:
• CANCELLED accounts: Data retained per policy for reactivation option
• You may request deletion of your data by contacting us
• Some data may be retained for legal compliance requirements

Audit Trail:
• Authentication events logged for security purposes
• Transaction records maintained for billing accuracy
• Immutable records for compliance and dispute resolution`,
  },
  "user-rights": {
    title: "Your Rights",
    content: `Access and Portability:
• Request a copy of your personal data
• Download your contract analyses and generated documents
• Export your custom playbook configurations

Correction and Deletion:
• Update your account information at any time
• Request correction of inaccurate data
• Request deletion of your account and associated data

Data Processing:
• Object to certain processing activities
• Request restriction of processing
• Withdraw consent where applicable

GDPR Rights (EU Residents):
• Right to be informed about data processing
• Right to access your personal data
• Right to rectification of inaccurate data
• Right to erasure ("right to be forgotten")
• Right to restrict processing
• Right to data portability
• Right to object to processing
• Rights related to automated decision-making

For GDPR Article 28 processor obligations and international data transfer details, see our Data Processing Agreement at bitsbound.com/dpa.`,
  },
  cookies: {
    title: "Cookies and Local Storage",
    content: `Essential Storage:
• Customer ID stored in localStorage for session continuity
• JWT authentication token for secure session management
• Preferences for data residency selection

We do not use:
• Third-party advertising cookies
• Cross-site tracking cookies
• Social media tracking pixels

Browser Settings:
• You can configure your browser to refuse cookies
• Disabling localStorage may impact service functionality`,
  },
  security: {
    title: "Security Practices",
    content: `Authentication Security:
• Password hashing with bcrypt (10+ rounds per NIST 800-63B §5.1.1.2)
• JWT tokens with 7-day expiration (PCI DSS 8.1.8 compliant)
• Failed login attempt logging (SOX §404 compliant)
• API key authentication for administrative endpoints
• Stripe webhook signature verification

Data Protection:
• HTTPS encryption for all data in transit
• Customer data isolation by database (GDPR Article 32)
• Input sanitization with DOMPurify
• XML entity stripping to prevent XXE attacks
• CORS restrictions (no wildcard access)

Infrastructure Security:
• Hosted on SOC 2 compliant platforms (Vercel, Render, MongoDB Atlas)
• Environment-based secret management
• Database health monitoring with automatic reconnection`,
  },
  children: {
    title: "Children's Privacy",
    content: `BitsBound is a professional service for legal professionals and businesses. Our services are not directed to individuals under the age of 18.

We do not knowingly collect personal information from children under 18. If you believe we have inadvertently collected such information, please contact us immediately at privacy@bitsbound.com.`,
  },
  changes: {
    title: "Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by:

• Posting the new Privacy Policy on this page
• Updating the "Last Updated" date
• Sending an email notification for significant changes

Your continued use of our services after changes become effective constitutes acceptance of the revised policy.`,
  },
  contact: {
    title: "Contact Us",
    content: `For privacy-related inquiries or to exercise your rights:

BitsBound, Inc.
A Taytrom Company
Philadelphia, Pennsylvania

Email: admin@bitsbound.com

For general inquiries:
Website: https://www.bitsbound.com

We aim to respond to all privacy requests within 30 days.`,
  },
};

// ════════════════════════════════════════════════════════════════════════════════
// DEFINITIONS 'D' - Section Icons
// ════════════════════════════════════════════════════════════════════════════════

const SECTION_ICONS: Record<string, string> = {
  overview: "🛡️",
  "data-collection": "📋",
  "data-storage": "💾",
  "data-usage": "⚙️",
  "third-parties": "🤝",
  "data-retention": "📅",
  "user-rights": "✊",
  cookies: "🍪",
  security: "🔐",
  children: "👶",
  changes: "📝",
  contact: "📧",
};

// ════════════════════════════════════════════════════════════════════════════════
// FUNCTION - Pod_Privacy Component
// ════════════════════════════════════════════════════════════════════════════════

function Pod_Privacy() {
  // DECLARATIONS - State Management
  const [activeSection, setActiveSection] = useState("overview");

  // OPERATIONS - Handler Functions
  const handleSectionChange = useCallback((section: string) => {
    setActiveSection(section);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownload = useCallback(() => {
    const privacyText = Object.entries(PRIVACY_CONTENT)
      .map(([_key, section]) => `${section.title}\n${'='.repeat(section.title.length)}\n${section.content}`)
      .join("\n\n");
    const blob = new Blob([privacyText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "BitsBound_Privacy_Policy.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // PRE-RENDER - Page View Logging
  useEffect(() => {
    console.info("[Privacy] Page viewed");
  }, []);

  // RENDER - Return Pea with Props
  return (
    <Pea_Privacy
      activeSection={activeSection}
      privacyContent={PRIVACY_CONTENT}
      sectionIcons={SECTION_ICONS}
      onSectionChange={handleSectionChange}
      onPrint={handlePrint}
      onDownload={handleDownload}
    />
  );
}

// EXPORTS
export default Pod_Privacy;